import { AlertTriangle, CheckCircle2, Info, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { BrandMark } from '@/components/BrandMark';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

type Tone = 'info' | 'error' | 'success';

const TONE: Record<Tone, { icon: LucideIcon; ring: string; glow: string }> = {
  info: {
    icon: Info,
    ring: 'border-primary/30 bg-primary/12 text-primary-foreground',
    glow: 'shadow-[0_0_50px_-12px_var(--primary)]',
  },
  error: {
    icon: AlertTriangle,
    ring: 'border-[color-mix(in_oklab,var(--destructive)_40%,transparent)] bg-[color-mix(in_oklab,var(--destructive)_14%,transparent)] text-[color-mix(in_oklab,var(--destructive)_80%,white)]',
    glow: 'shadow-[0_0_50px_-12px_var(--destructive)]',
  },
  success: {
    icon: CheckCircle2,
    ring: 'border-[color-mix(in_oklab,var(--success)_40%,transparent)] bg-[color-mix(in_oklab,var(--success)_14%,transparent)] text-[var(--success)]',
    glow: 'shadow-[0_0_50px_-12px_var(--success)]',
  },
};

/**
 * The terminal screen for every dead end: an expired link, a finished
 * interview, a call we could not rescue. `.notice` / `.notice-title` are load
 * bearing — the browser checks in scripts/ read the outcome of a run from them.
 */
export function Notice({
  title,
  children,
  tone = 'info',
  actions,
}: {
  title: string;
  children?: ReactNode;
  tone?: Tone;
  actions?: ReactNode;
}) {
  const { icon: Icon, ring, glow } = TONE[tone];

  return (
    <main className="grid min-h-full place-items-center p-6">
      <Card
        className={cn(
          'notice animate-in fade-in-0 slide-in-from-bottom-2 w-full max-w-md p-8 text-center duration-500',
          `is-${tone}`,
        )}
      >
        <BrandMark className="mb-7 justify-center" size="sm" />

        <div className={cn('mx-auto grid size-12 place-items-center rounded-2xl border', ring, glow)}>
          <Icon className="size-5.5" strokeWidth={2} />
        </div>

        <h1 className="notice-title mt-5 text-xl font-semibold tracking-tight text-balance">{title}</h1>

        <div className="text-muted-foreground mt-2.5 space-y-2 text-sm text-pretty">{children}</div>

        {actions && <div className="mt-6 flex flex-wrap justify-center gap-2">{actions}</div>}
      </Card>
    </main>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <main className="grid min-h-full place-items-center p-6">
      <div className="notice flex flex-col items-center gap-5">
        <BrandMark size="lg" showWordmark={false} className="animate-pulse" />
        <div className="text-muted-foreground flex items-center gap-2.5 text-sm">
          <Spinner className="size-4" />
          <span className="notice-title">{label}</span>
        </div>
      </div>
    </main>
  );
}
